import React, { useState, useEffect } from 'react';
import { GetRequest } from 'components/common';

const AirtablePreview = ({
  dataSourceId,
  baseId,
  tableId,
  tables = [],
  onTableSelect,
  onCellSelect,
  selectedFieldId,
  selectedRecordId,
  maxRecords = 50
}) => {
  const [data, setData] = useState({ records: [], fields: [] });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!dataSourceId || !baseId || !tableId) {
      setData({ records: [], fields: [] });
      return;
    }

    setLoading(true);
    setError(null);

    GetRequest({
      url: `/quote-builder/data-sources/${dataSourceId}/base/${baseId}/table/${tableId}/records`,
      data: { max_records: maxRecords },
      callback: (result) => {
        setLoading(false);
        if (result.error) {
          setError(result.error);
        } else {
          setData({
            records: result.records || [],
            fields: result.fields || [],
            tableName: result.table_name
          });
        }
      }
    });
  }, [dataSourceId, baseId, tableId, maxRecords]);

  const handleCellClick = (record, field) => {
    const fieldValue = record.fields[field.id];
    const displayValue = formatCellValue(fieldValue);

    // Get the primary field value (first field) for display as record name
    const primaryFieldId = data.fields[0]?.id;
    const recordName = primaryFieldId ? formatCellValue(record.fields[primaryFieldId]) : record.id;

    onCellSelect({
      recordId: record.id,
      recordName: recordName,
      fieldId: field.id,
      fieldName: field.name,
      value: displayValue
    });
  };

  const formatCellValue = (value) => {
    if (value === null || value === undefined) return '';
    if (Array.isArray(value)) {
      // Handle linked records, attachments, etc.
      if (value.length === 0) return '';
      if (typeof value[0] === 'object') {
        return value.map(v => v.name || v.url || JSON.stringify(v)).join(', ');
      }
      return value.join(', ');
    }
    if (typeof value === 'object') {
      return value.name || value.email || JSON.stringify(value);
    }
    if (typeof value === 'boolean') {
      return value ? 'Yes' : 'No';
    }
    return String(value);
  };

  const isCellSelected = (recordId, fieldId) => {
    return selectedRecordId === recordId && selectedFieldId === fieldId;
  };

  if (loading) {
    return (
      <div className="airtable-preview-loading" style={{ padding: '20px', textAlign: 'center' }}>
        <i className="fa fa-spinner fa-spin"></i> {t('quote_builder.loading_airtable_data')}
      </div>
    );
  }

  if (error) {
    return (
      <div className="airtable-preview-error alert alert-danger">
        <i className="fa fa-exclamation-circle"></i> {error}
      </div>
    );
  }

  if (data.records.length === 0) {
    return (
      <div className="airtable-preview-empty" style={{ padding: '20px', textAlign: 'center', color: '#999' }}>
        {t('quote_builder.no_data_in_table')}
      </div>
    );
  }

  const tableTabsStyle = {
    display: 'flex',
    background: '#f0f0f0',
    borderTop: '1px solid #ccc',
    padding: '0',
    overflowX: 'auto'
  };

  const tableTabStyle = (isActive) => ({
    padding: '8px 16px',
    border: 'none',
    borderRight: '1px solid #ccc',
    background: isActive ? '#fff' : '#e8e8e8',
    cursor: 'pointer',
    fontSize: '13px',
    fontWeight: isActive ? '500' : 'normal',
    color: isActive ? '#18BFFF' : '#5f6368',
    whiteSpace: 'nowrap',
    borderBottom: isActive ? '2px solid #18BFFF' : '2px solid transparent',
    marginBottom: isActive ? '-1px' : '0',
    transition: 'all 0.15s ease'
  });

  return (
    <div className="airtable-preview">
      <style>
        {`
          .airtable-preview-table th,
          .airtable-preview-table td {
            border: 1px solid #ccc !important;
          }
          .airtable-preview-table td.selected-cell {
            border: 2px solid #0099cc !important;
          }
        `}
      </style>
      <div className="airtable-preview-header" style={{ marginBottom: '10px' }}>
        <small className="text-muted">
          <i className="fa fa-hand-pointer-o"></i> {t('quote_builder.click_cell_to_select')}
        </small>
      </div>
      <div
        className="airtable-preview-container"
        style={{
          maxHeight: '300px',
          overflow: 'auto',
          border: '1px solid #ccc',
          borderRadius: tables.length > 1 ? '4px 4px 0 0' : '4px'
        }}
      >
        <table
          className="table table-sm airtable-preview-table"
          style={{
            marginBottom: 0,
            fontSize: '12px',
            tableLayout: 'auto',
            borderCollapse: 'collapse',
            border: '1px solid #ccc'
          }}
        >
          <thead style={{ position: 'sticky', top: 0, background: '#f5f5f5', zIndex: 1 }}>
            <tr>
              <th style={{
                minWidth: '30px',
                textAlign: 'center',
                background: '#e9e9e9',
                fontWeight: 'bold',
                position: 'sticky',
                left: 0,
                zIndex: 2,
                border: '1px solid #ccc'
              }}>#</th>
              {data.fields.map((field) => (
                <th
                  key={field.id}
                  style={{
                    minWidth: '80px',
                    textAlign: 'left',
                    background: '#e9e9e9',
                    fontWeight: 'bold',
                    fontSize: '11px',
                    border: '1px solid #ccc'
                  }}
                  title={`${field.name} (${field.type})`}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                    <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {field.name}
                    </span>
                    <span style={{ color: '#999', fontSize: '10px' }}>
                      {getFieldTypeIcon(field.type)}
                    </span>
                  </div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {data.records.map((record, rowIdx) => (
              <tr key={record.id}>
                <td style={{
                  textAlign: 'center',
                  background: '#e9e9e9',
                  fontWeight: 'bold',
                  position: 'sticky',
                  left: 0,
                  zIndex: 1,
                  fontSize: '10px',
                  color: '#666',
                  border: '1px solid #ccc'
                }}>
                  {rowIdx + 1}
                </td>
                {data.fields.map((field) => {
                  const cellValue = formatCellValue(record.fields[field.id]);
                  const isSelected = isCellSelected(record.id, field.id);
                  return (
                    <td
                      key={field.id}
                      className={isSelected ? 'selected-cell' : ''}
                      onClick={() => handleCellClick(record, field)}
                      style={{
                        cursor: 'pointer',
                        minWidth: '80px',
                        maxWidth: '200px',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        padding: '4px 8px',
                        background: isSelected ? '#18BFFF' : 'white',
                        color: isSelected ? 'white' : 'inherit',
                        transition: 'background-color 0.15s ease'
                      }}
                      title={cellValue}
                      onMouseEnter={(e) => {
                        if (!isSelected) {
                          e.target.style.background = '#e6f9ff';
                        }
                      }}
                      onMouseLeave={(e) => {
                        if (!isSelected) {
                          e.target.style.background = 'white';
                        }
                      }}
                    >
                      {cellValue || <span style={{ color: '#ccc', fontStyle: 'italic' }}>empty</span>}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {tables.length > 1 && (
        <div className="table-tabs" style={tableTabsStyle}>
          {tables.map(table => (
            <button
              key={table.id}
              type="button"
              style={tableTabStyle(table.id === tableId)}
              onClick={() => onTableSelect && onTableSelect(table)}
              onMouseEnter={(e) => {
                if (table.id !== tableId) {
                  e.target.style.background = '#f5f5f5';
                }
              }}
              onMouseLeave={(e) => {
                if (table.id !== tableId) {
                  e.target.style.background = '#e8e8e8';
                }
              }}
            >
              {table.name}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

// Helper function to get an icon/indicator for field types
const getFieldTypeIcon = (type) => {
  const typeIcons = {
    'singleLineText': 'Abc',
    'multilineText': 'Txt',
    'email': '@',
    'url': 'URL',
    'number': '#',
    'currency': '$',
    'percent': '%',
    'singleSelect': '1',
    'multipleSelects': 'n',
    'date': 'D',
    'dateTime': 'DT',
    'checkbox': '?',
    'rating': '*',
    'duration': 'H',
    'phoneNumber': 'P',
    'formula': 'fx',
    'rollup': 'R',
    'count': 'C',
    'lookup': 'L',
    'multipleRecordLinks': 'L',
    'multipleAttachments': 'A',
    'barcode': 'B',
    'button': 'B',
    'createdTime': 'CT',
    'lastModifiedTime': 'MT',
    'createdBy': 'CB',
    'lastModifiedBy': 'MB',
    'autoNumber': '#'
  };
  return typeIcons[type] || '';
};

export default AirtablePreview;
